import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '@/shared/api/axios';
import { useAuth } from '@/shared/hooks/useAuth';
import type {
  CreateJobPayload,
  JobApplication,
  JobPosting,
  JobStatus,
  LinkedOfferLetterSummary,
  RecruitmentSummary,
  StageMovePayload,
  StageMoveResult,
} from './types';

export interface CompanyProfile {
  _id: string;
  name: string;
  legalName?: string;
  gstin?: string;
  pan?: string;
  cin?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  email?: string;
  phone?: string;
  website?: string;
  logo?: string;
  logoUrl?: string;
  signatureUrl?: string;
  gst?: string;
  bankAccName?: string;
  bankAccNo?: string;
  bankName?: string;
  bankBranch?: string;
  bankIfsc?: string;
  bankUpi?: string;
  bankDetails?: {
    accountName?: string;
    accountNumber?: string;
    ifsc?: string;
    bankName?: string;
    branch?: string;
  };
  registeredAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
  };
  industry?: string;
  officialEmail?: string;
  timezone?: string;
  defaultCurrency?: string;
  fiscalYearStartMonth?: number;
  workingHours?: {
    start?: string;
    end?: string;
  };
  workWeek?: {
    workDays?: number[];
    hoursPerDay?: number;
  };
}

export function useCompanyProfile() {
  const { organization } = useAuth();
  
  return useQuery({
    queryKey: ['companyProfile', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;
      try {
        // Get organization by ID - the route is /api/v1/goodday/organizations/:id
        // The axios client strips /api/ so we use /organizations/:id
        const { data } = await api.get<CompanyProfile>(`/organizations/${organization.id}`);
        // The backend route returns the organization directly
        return data;
      } catch {
        return null;
      }
    },
    enabled: !!organization?.id,
  });
}

/* ── Recruitment ──────────────────────────────────────────────────────────── */

const RECRUITMENT_BASE = '/api/recruitment';
const OFFERS_BASE = '/api/hr-offers';

export const recruitmentKeys = {
  root: ['recruitment'] as const,
  summary: ['recruitment', 'summary'] as const,
  jobs: ['recruitment', 'jobs'] as const,
  applications: (jobId: string | null) => ['recruitment', 'applications', jobId] as const,
  linkedOffer: (applicationId: string | null) => ['recruitment', 'linked-offer', applicationId] as const,
};

export interface RecruitmentAccess {
  canRead: boolean;
  canManageJobs: boolean;
  canManageCandidates: boolean;
}

/** What the signed-in person may do in Recruitment (decided by the server from their HR grants). */
export function useRecruitmentAccess() {
  return useQuery({
    queryKey: ['recruitment', 'access'] as const,
    queryFn: async () => (await api.get<RecruitmentAccess>(`${RECRUITMENT_BASE}/access`)).data,
    staleTime: 60_000,
  });
}

/** Open a candidate's resume. The file needs the login token, so it is fetched and shown from a blob. */
export async function openResume(applicationId: string) {
  const { data } = await api.get<Blob>(`${RECRUITMENT_BASE}/applications/${applicationId}/resume`, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function useRecruitmentSummary() {
  return useQuery({
    queryKey: recruitmentKeys.summary,
    queryFn: async () => (await api.get<RecruitmentSummary>(`${RECRUITMENT_BASE}/summary`)).data,
    staleTime: 30_000,
  });
}

export function useJobPostings() {
  return useQuery({
    queryKey: recruitmentKeys.jobs,
    queryFn: async () => (await api.get<JobPosting[]>(`${RECRUITMENT_BASE}/jobs`)).data,
  });
}

export function useJobApplications(jobId: string | null) {
  return useQuery({
    queryKey: recruitmentKeys.applications(jobId),
    queryFn: async () => {
      if (!jobId) {
        return (await api.get<JobApplication[]>(`${RECRUITMENT_BASE}/applications`)).data;
      }
      return (await api.get<JobApplication[]>(`${RECRUITMENT_BASE}/jobs/${jobId}/applications`)).data;
    },
    enabled: true,
  });
}

export function useRecruitmentMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: recruitmentKeys.root });

  const createJob = useMutation({
    mutationFn: async (payload: CreateJobPayload) =>
      (await api.post<JobPosting>(`${RECRUITMENT_BASE}/jobs`, payload)).data,
    onSuccess: refresh,
  });

  const updateJob = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; status?: JobStatus } & Partial<CreateJobPayload>) =>
      (await api.patch<JobPosting>(`${RECRUITMENT_BASE}/jobs/${id}`, payload)).data,
    onSuccess: refresh,
  });

  const addApplication = useMutation({
    mutationFn: async ({
      jobId,
      resume,
      ...payload
    }: {
      jobId: string;
      candidateName: string;
      email?: string;
      phone?: string;
      coverNote?: string;
      source?: string;
      resume?: File | null;
    }) => {
      if (resume) {
        const form = new FormData();
        form.append('candidateName', payload.candidateName);
        if (payload.email) form.append('email', payload.email);
        if (payload.phone) form.append('phone', payload.phone);
        if (payload.coverNote) form.append('coverNote', payload.coverNote);
        if (payload.source) form.append('source', payload.source);
        form.append('resume', resume);
        return (
          await api.post<JobApplication>(`${RECRUITMENT_BASE}/jobs/${jobId}/applications`, form, {
            headers: { 'Content-Type': null },
          })
        ).data;
      }
      return (await api.post<JobApplication>(`${RECRUITMENT_BASE}/jobs/${jobId}/applications`, payload)).data;
    },
    onSuccess: refresh,
  });

  const screenApplication = useMutation({
    mutationFn: async (id: string) =>
      (await api.post<JobApplication>(`${RECRUITMENT_BASE}/applications/${id}/screen`)).data,
    onSuccess: refresh,
  });

  const moveStage = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & StageMovePayload) =>
      (await api.post<StageMoveResult>(`${RECRUITMENT_BASE}/applications/${id}/stage`, payload)).data,
    onSuccess: (_data, variables) => {
      refresh();
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.linkedOffer(variables.id) });
    },
  });

  return { createJob, updateJob, addApplication, screenApplication, moveStage };
}

export function useLinkedOfferLetter(applicationId: string | null) {
  return useQuery({
    queryKey: recruitmentKeys.linkedOffer(applicationId),
    queryFn: async () => {
      try {
        const { data } = await api.get<LinkedOfferLetterSummary>(`${OFFERS_BASE}/by-application/${applicationId}`);
        return data;
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!applicationId,
  });
}

/* ── Master Data: Departments ─────────────────────────────────────────────── */

export interface MasterDepartment {
  label: string;
  value: string;
}

export const departmentKeys = {
  list: ['workspace', 'departments'] as const,
};

export function useMasterDepartments() {
  return useQuery({
    queryKey: departmentKeys.list,
    queryFn: async () => {
      const { data } = await api.get<{ departments: MasterDepartment[] }>('/api/workspace/org/departments');
      return data.departments;
    },
    staleTime: 60_000,
  });
}

export function useUpdateDepartments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (departments: MasterDepartment[]) => {
      const { data } = await api.post<{ departments: MasterDepartment[] }>('/api/workspace/org/departments', { departments });
      return data.departments;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.list });
    },
  });
}